/**
 * Aggregator - orquestra scroll, click, idle, exitIntent, visibility;
 * dispara eventos de behavior via Queue (enqueueEvent com prioridade medium/low).
 */

import type { GomarkePixelConfig } from '../types';
import type {
  BehaviorState,
  BehaviorEngineConfig,
  ScrollState,
  ClickState,
} from './types';
import { getSession, getVisitor, setSessionEngagementScore } from '../core';
import { enqueueEvent } from '../core/queue';
import { registerUnloadHook } from '../core/unload';
import type { QueuePriority } from '../core/queue';
import {
  initScrollBehavior,
  getScrollState,
  resetScrollState,
  destroyScrollBehavior,
} from './scroll';
import {
  initClickBehavior,
  getClickState,
  resetClickState,
  destroyClickBehavior,
} from './click';
import {
  initIdleBehavior,
  getIdleState,
  resetIdleState,
  destroyIdleBehavior,
} from './idle';
import {
  initExitIntentBehavior,
  getExitIntentFired,
  resetExitIntentState,
  destroyExitIntentBehavior,
} from './exitIntent';
import {
  initVisibilityBehavior,
  getVisibilityState,
  resetVisibilityState,
  destroyVisibilityBehavior,
} from './visibility';
import { computeEngagement } from './engagement';
import {
  initViewportBehavior,
  destroyViewportBehavior,
  eventTargetDescriptor,
  getClickPosition,
  getElementDataForClick,
} from './viewport';
import { BehaviorPhase, type BehaviorCriticalEventType } from './types';
import {
  getBehaviorStateMachine,
  resetBehaviorStateMachine,
} from './stateMachine';
import {
  addBehaviorCriticalEvent,
  clearBehaviorBuffer,
  getBehaviorBufferStats,
} from './buffer';
import {
  initBehaviorRateLimit,
  destroyBehaviorRateLimit,
  getMouseMovementCount,
  getKeypressCount,
} from './rateLimit';
import {
  startBehaviorHeartbeat,
  stopBehaviorHeartbeat,
  resetHeartbeatLastSnapshot,
  flushBehaviorSnapshotNow,
  setHeartbeatPresenceOnlyMode,
} from './heartbeat';
import { setBehaviorDirty } from './dirty';
import type { BehaviorSnapshot } from './types';

let engineConfig: {
  config: GomarkePixelConfig;
  version: string;
  behavior?: BehaviorEngineConfig;
} | null = null;

function getVersion(): string {
  return engineConfig?.version ?? '1.0.0';
}

function buildBehaviorState(): BehaviorState {
  const idleState = getIdleState();
  return {
    scroll: getScrollState(),
    click: getClickState(),
    idle: idleState,
    visibility: getVisibilityState(),
    exitIntentFired: getExitIntentFired(),
    lastActivityAt: Math.max(
      getScrollState().lastScrollAt,
      getClickState().lastClickAt,
      idleState.lastActivityAt
    ),
  };
}

type BehaviorEventType =
  | 'USER_IDLE'
  | 'EXIT_INTENT'
  | 'BEHAVIOR_SUMMARY'
  | 'SCROLL'
  | 'PAGE_CLICK'
  | 'VISIBILITY_CHANGE';

function getPriorityForBehaviorEvent(type: BehaviorEventType): QueuePriority {
  if (
    type === 'EXIT_INTENT' ||
    type === 'VISIBILITY_CHANGE' ||
    type === 'USER_IDLE'
  )
    return 'medium';
  return 'low';
}

function sendBehaviorEventToApi(
  type: BehaviorEventType,
  eventData: Record<string, unknown>
): void {
  const cfg = engineConfig?.config;
  const session = getSession();
  const visitor = getVisitor();
  if (!cfg || !session || !visitor) return;

  const params = {
    category: 'ENGAGEMENT' as const,
    type,
    config: cfg,
    sessionId: session.sessionId,
    visitorId: visitor.visitorId,
    version: getVersion(),
    eventData: {
      ...eventData,
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      path: typeof window !== 'undefined' ? window.location.pathname : '',
      timestamp: Date.now(),
    },
  };
  enqueueEvent(params, getPriorityForBehaviorEvent(type));
}

function sendBehaviorEvent(
  type: 'USER_IDLE' | 'EXIT_INTENT' | 'BEHAVIOR_SUMMARY',
  eventData: Record<string, unknown>
): void {
  sendBehaviorEventToApi(type, eventData);
}

function sendScrollEvent(state: ScrollState): void {
  setBehaviorDirty();
  sendBehaviorEventToApi('SCROLL', {
    max_scroll_percent: state.maxScrollPercent,
    scroll_events: state.scrollEvents,
    scroll_velocity_avg: state.scrollVelocityAvg,
    last_scroll_at: state.lastScrollAt,
  });
}

function sendPageClickEvent(state: ClickState, event?: MouseEvent): void {
  setBehaviorDirty();
  const eventData: Record<string, unknown> = {
    click_count: state.clickCount,
    rage_click_count: state.rageClickCount,
    last_click_at: state.lastClickAt,
  };
  if (event) {
    eventData.target_desc = eventTargetDescriptor(event.target);
    eventData.current_target_desc = eventTargetDescriptor(event.currentTarget);
    const pos = getClickPosition(event);
    if (pos) Object.assign(eventData, pos);
    if (event.target instanceof Element) {
      eventData.element_data = getElementDataForClick(event.target);
      eventData.element_tag = (event.target as Element).tagName.toLowerCase();
    }
  }
  sendBehaviorEventToApi('PAGE_CLICK', eventData);
}

function sendVisibilityChangeEvent(isVisible: boolean): void {
  const vs = getVisibilityState();
  sendBehaviorEventToApi('VISIBILITY_CHANGE', {
    is_visible: isVisible,
    visible_since: vs.visibleSince,
    hidden_at: vs.hiddenAt,
  });
}

function getPageId(): string {
  return typeof window !== 'undefined'
    ? window.location.pathname || window.location.href
    : '';
}

function pushCritical(
  type: BehaviorCriticalEventType,
  payload?: Record<string, unknown>
): void {
  const session = getSession();
  const visitor = getVisitor();
  if (!session || !visitor) return;
  addBehaviorCriticalEvent(
    type,
    visitor.visitorId,
    session.sessionId,
    getPageId(),
    payload
  );
}

function onIdle(): void {
  setBehaviorDirty();
  getBehaviorStateMachine().transition(BehaviorPhase.IDLE);
  pushCritical('idle_start');
  const state = buildBehaviorState();
  sendBehaviorEvent('USER_IDLE', {
    active_ms: state.idle.activeMs,
    idle_threshold_ms: engineConfig?.behavior?.idleThresholdMs ?? 60000,
  });
}

function onIdleEnd(): void {
  setBehaviorDirty();
  getBehaviorStateMachine().transition(BehaviorPhase.ACTIVE);
  pushCritical('idle_end');
}

function onExitIntent(): void {
  const session = getSession();
  const visitor = getVisitor();
  if (session && visitor) {
    flushBehaviorSnapshotNow(visitor.visitorId, session.sessionId);
  }
  getBehaviorStateMachine().transition(BehaviorPhase.EXITING);
  pushCritical('exit_intent');
  const state = buildBehaviorState();
  sendBehaviorEvent('EXIT_INTENT', {
    max_scroll_percent: state.scroll.maxScrollPercent,
    click_count: state.click.clickCount,
    active_ms: state.idle.activeMs,
  });
}

let spaUninstall: (() => void) | null = null;
let beforeunloadUninstall: (() => void) | null = null;
let didFinalFlush = false;

function onVisibilityChange(): void {
  const vs = getVisibilityState();
  if (!vs.isVisible && vs.hiddenAt != null) {
    setBehaviorDirty();
    const session = getSession();
    const visitor = getVisitor();
    if (session && visitor && !didFinalFlush) {
      didFinalFlush = true;
      flushBehaviorSnapshotNow(visitor.visitorId, session.sessionId);
    }
    setHeartbeatPresenceOnlyMode(true);
    getBehaviorStateMachine().transition(BehaviorPhase.HIDDEN);
    pushCritical('page_hidden');
    const state = buildBehaviorState();
    const { score, level } = computeEngagement(state, {
      weights: engineConfig?.behavior?.scrollWeights,
      thresholds: engineConfig?.behavior?.engagementThresholds,
      interaction: {
        keypresses: getKeypressCount(),
        mouseMovements: getMouseMovementCount(),
      },
    });
    sendBehaviorEvent('BEHAVIOR_SUMMARY', {
      max_scroll_percent: state.scroll.maxScrollPercent,
      scroll_events: state.scroll.scrollEvents,
      scroll_velocity_avg: state.scroll.scrollVelocityAvg,
      click_count: state.click.clickCount,
      rage_click_count: state.click.rageClickCount,
      active_ms: state.idle.activeMs,
      idle_ms: state.idle.idleMs,
      exit_intent_fired: state.exitIntentFired,
      engagement_score: score,
      engagement_level: level,
    });
    if (session) {
      setSessionEngagementScore(session, score);
    }
  } else if (vs.isVisible) {
    setBehaviorDirty();
    setHeartbeatPresenceOnlyMode(false);
    const sm = getBehaviorStateMachine();
    if (sm.transition(BehaviorPhase.ACTIVE)) pushCritical('visible');
  }
}

export function initBehaviorEngine(options: {
  config: GomarkePixelConfig;
  version: string;
  behavior?: BehaviorEngineConfig;
}): void {
  if (typeof window === 'undefined') return;
  engineConfig = {
    config: options.config,
    version: options.version,
    behavior: options.behavior,
  };
  didFinalFlush = false;

  const behavior = options.behavior ?? {};
  if (behavior.enabled === false) return;

  if (behavior.heartbeatOnly === true) {
    startBehaviorHeartbeat(
      () => getVisitor()?.visitorId ?? undefined,
      () => getSession()?.sessionId ?? undefined,
      undefined,
      engineConfig.config,
      engineConfig.version
    );
    return;
  }

  const idleThresholdMs = behavior.idleThresholdMs ?? 60 * 1000;
  const rageClickWindowMs = behavior.rageClickWindowMs;

  initScrollBehavior(undefined, sendScrollEvent);
  initClickBehavior(undefined, {
    rageClickWindowMs,
    onPageClickEvent: (state, event) => sendPageClickEvent(state, event),
  });
  initIdleBehavior(idleThresholdMs, onIdle, onIdleEnd);
  initExitIntentBehavior(onExitIntent);
  initVisibilityBehavior(s => {
    sendVisibilityChangeEvent(s.isVisible);
    if (!s.isVisible) onVisibilityChange();
    else onVisibilityChange();
  });
  initBehaviorRateLimit();
  const enrichSnapshotWithEngagement = (
    snapshot: BehaviorSnapshot
  ): BehaviorSnapshot => {
    const state = buildBehaviorState();
    const { score, level } = computeEngagement(state, {
      weights: behavior.scrollWeights,
      thresholds: behavior.engagementThresholds,
      interaction: {
        keypresses: getKeypressCount(),
        mouseMovements: getMouseMovementCount(),
      },
    });
    return { ...snapshot, engagement: { score, level } };
  };
  startBehaviorHeartbeat(
    () => getVisitor()?.visitorId ?? undefined,
    () => getSession()?.sessionId ?? undefined,
    enrichSnapshotWithEngagement,
    engineConfig.config,
    engineConfig.version
  );

  if (behavior.viewport?.enabled !== false && behavior.viewport?.selector) {
    initViewportBehavior(options.config, options.version, behavior.viewport);
  }

  if (typeof window !== 'undefined') {
    beforeunloadUninstall = registerUnloadHook(() => {
      try {
        const session = getSession();
        const visitor = getVisitor();
        if (session && visitor && !didFinalFlush) {
          didFinalFlush = true;
          flushBehaviorSnapshotNow(visitor.visitorId, session.sessionId);
        }
      } catch {
        // noop
      }
    });
  }

  // SPA: resetar estado por página ao mudar de URL
  if (typeof window !== 'undefined') {
    let lastPath = window.location.pathname;
    const onUrlChange = (): void => {
      const path = window.location.pathname;
      if (path !== lastPath) {
        lastPath = path;
        resetForNewPage();
      }
    };
    window.addEventListener('popstate', onUrlChange);
    const origPush = history.pushState.bind(history);
    const origReplace = history.replaceState.bind(history);
    history.pushState = function (...args) {
      origPush(...args);
      onUrlChange();
    };
    history.replaceState = function (...args) {
      origReplace(...args);
      onUrlChange();
    };
    spaUninstall = () => {
      window.removeEventListener('popstate', onUrlChange);
      history.pushState = origPush;
      history.replaceState = origReplace;
    };
  }
}

export function getBehaviorState(): BehaviorState {
  return buildBehaviorState();
}

/**
 * Gera snapshot atual do behavior e enfileira na Queue (prioridade low).
 * API explícita para "enqueue behavior snapshot" (Bloco 3).
 */
export function enqueueBehaviorSnapshot(): void {
  const session = getSession();
  const visitor = getVisitor();
  if (session && visitor) {
    flushBehaviorSnapshotNow(visitor.visitorId, session.sessionId);
  }
}

/** Reseta estado por página (scroll, click, exit intent, visibility). Idle e engagement mantidos por sessão. */
export function resetForNewPage(): void {
  resetScrollState();
  resetClickState();
  resetExitIntentState();
  resetVisibilityState();
  resetBehaviorStateMachine();
  resetHeartbeatLastSnapshot();
}

export function resetBehaviorState(): void {
  resetScrollState();
  resetClickState();
  resetIdleState();
  resetExitIntentState();
  resetVisibilityState();
}

export function destroyBehaviorEngine(): void {
  if (beforeunloadUninstall) {
    beforeunloadUninstall();
  }
  if (spaUninstall) {
    spaUninstall();
    spaUninstall = null;
  }
  stopBehaviorHeartbeat();
  destroyBehaviorRateLimit();
  clearBehaviorBuffer();
  resetBehaviorStateMachine();
  destroyScrollBehavior();
  destroyClickBehavior();
  destroyIdleBehavior();
  destroyExitIntentBehavior();
  destroyVisibilityBehavior();
  destroyViewportBehavior();
  engineConfig = null;
}

/** Estatísticas do buffer (pré-fila) para simulação/debug. */
export function getBehaviorBufferStatsExport(): ReturnType<
  typeof getBehaviorBufferStats
> {
  return getBehaviorBufferStats();
}
